import { scenarioStatusSvc as service, scenarioSvc as scenarioMgmtSvc } from "services/serviceStorage";
import { wrapRequest } from "common/utils";
import { createAsyncThunk } from "@reduxjs/toolkit";
import dayjs from "dayjs";
import { ScenarioStatus } from './types';
import { BaseApiResponse, Scenario } from "common/types";
import type { RootState } from "store";

export const fetchStatuses = createAsyncThunk<{ data: ScenarioStatus[]; total: number }, void, { state: RootState }>(
    'scenarioStatus/fetchStatuses',
    async (_, { getState }) => {
        const { filter } = getState().scenarioStatusSlice.gridSource;

        const { data } = await wrapRequest<BaseApiResponse<{ items: ScenarioStatus[]; totalCount: number }>>({
            handler: () => service.getStatuses(filter),
            showNotifications: true
        });

        return {
            data: data?.result.items.map(i => ({
                ...i,
                key: i.historyId,
                segmentStart: i.segmentStart && dayjs.utc(i.segmentStart).format("MM-DD-YYYY HH:mm"),
                segmentEnd: i.segmentEnd && dayjs.utc(i.segmentEnd).format("MM-DD-YYYY HH:mm"),
                startWork: i.startWork && dayjs.utc(i.startWork).local().format("MM-DD-YYYY HH:mm"),
                psoTime: typeof i.psoTime === 'number' && i.psoTime > 0 ? dayjs.duration(i.psoTime, 'seconds').format('HH:mm:ss') : '',
                vmTime: typeof i.vmTime === 'number' && i.vmTime > 0 ? dayjs.duration(i.vmTime, 'seconds').format('HH:mm:ss') : '',
                errors: i.statusErrors?.map(e => ({
                    status: e.status,
                    startWork: e.startWork,
                    date: e.startWork && dayjs.utc(e.startWork).local().format("MM-DD-YYYY HH:mm"),
                    friendlyMessage: e.friendlyMessage,
                    message: e.friendlyMessage,
                    hasLogs: e.hasLogs,
                    historyId: e.historyId
                })),
            })) || [],
            total: data?.result.totalCount || 0,
        };
    }
);

export const manualError = createAsyncThunk<void, { scenarioId: number; segmentIds: number[] }>(
    'scenarioStatus/manualError',
    async ({ scenarioId, segmentIds }, { rejectWithValue }) => {
        const err = await wrapRequest<void>({
            handler: () => service.manualError({ scenarioId, segmentIds }),
            showNotifications: true
        });

        if (err.errors?.length || err.warnings?.length) {
            return rejectWithValue(err);
        }
    }
);

export const getLogs = createAsyncThunk<string, number>(
    'scenarioStatus/getLogs',
    async (historyId) => {
        const { data } = await wrapRequest<BaseApiResponse<string>>({
            handler: () => service.getLogs(historyId),
            showNotifications: true
        });

        return data?.result!;
    }
);

export const getErrorLogs = createAsyncThunk<string, number>(
    'scenarioStatus/getErrorLogs',
    async (historyId) => {
        const { data } = await wrapRequest<BaseApiResponse<string>>({
            handler: () => service.getErrorLogs(historyId),
            showNotifications: true
        });

        return data?.result!;
    }
);

export const getResults = createAsyncThunk<string[], number>(
    'scenarioStatus/getResults',
    async (historyId) => {
        const { data } = await wrapRequest<BaseApiResponse<string[]>>({
            handler: () => service.getResults(historyId),
            showNotifications: true
        });

        return data?.result || [];
    }
);

export const forceMerge = createAsyncThunk<void, number>(
    'scenarioStatus/forceMerge',
    async (scenarioId, { rejectWithValue }) => {
        const err = await wrapRequest<void>({
            handler: () => scenarioMgmtSvc.runForceMerge([scenarioId]),
            showNotifications: true
        });

        if (err.errors?.length || err.warnings?.length) {
            return rejectWithValue(err);
        }
    }
);

export const restartSegment = createAsyncThunk<void, number>(
    'scenarioStatus/restartSegment',
    async (historyId, { rejectWithValue }) => {
        const err = await wrapRequest<void>({
            handler: () => service.restartSegment(historyId),
            showNotifications: true
        });

        if (err.errors?.length || err.warnings?.length) {
            return rejectWithValue(err);
        }
    }
);

export const stopSegment = createAsyncThunk<void, number>(
    'scenarioStatus/stopSegment',
    async (historyId, { rejectWithValue }) => {
        const err = await wrapRequest<void>({
            handler: () => service.stopSegment(historyId),
            showNotifications: true
        });

        if (err.errors?.length || err.warnings?.length) {
            return rejectWithValue(err);
        }
    }
);

export const restartFailedSegments = createAsyncThunk<void, number>(
    'scenarioStatus/restartFailedSegments',
    async (scenarioId, { rejectWithValue }) => {
        const err = await wrapRequest<void>({
            handler: () => service.restartFailedSegments(scenarioId),
            showNotifications: true
        });

        if (err.errors?.length || err.warnings?.length) {
            return rejectWithValue(err);
        }
    }
);

export const getAdjoinScenarios = createAsyncThunk<number[], number>(
    'scenarioStatus/getAdjoinScenarios',
    async (scenarioId) => {
        const { data } = await wrapRequest<BaseApiResponse<number[]>>({
            handler: () => service.getAdjoinScenarios(scenarioId),
            showNotifications: true
        });

        return data?.result || [];
    }
);

export const fetchScenario = createAsyncThunk<Scenario, number>(
    'scenarioStatus/fetchScenario',
    async (scenarioId) => {
        const { data } = await wrapRequest<BaseApiResponse<Scenario>>({
            handler: () => scenarioMgmtSvc.getScenario(scenarioId),
            showNotifications: true
        });

        const scenario = data?.result!;

        return {
            ...scenario,
            useSpot: scenario.useSpot != null ? (scenario.useSpot ? 1 : 0) : null  // convert nullable boolean to int for UI
        };
    }
);
